import { useContext } from "react";
import Chapter from "@/components/Chapter";
import Reveal from "@/components/Reveal";
import { CONTACT } from "@/data/content";
import { SocialCards } from "@/components/Socials";
import { SampleModalContext } from "@/App";
import { ArrowRight, Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";

const OfficeCard = ({ office, city, note, testId, mapTestId }) => (
  <div data-testid={testId} className="flex h-full flex-col border border-navy/10 bg-cream">
    <div className="p-9">
      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-royal">{office.label}</p>
      <h3 className="mt-3 font-serif text-2xl font-semibold text-navy">{city}</h3>
      <address className="mt-4 text-sm not-italic leading-relaxed text-slate-600">
        {office.lines.map((l) => (
          <span key={l} className="block">{l}</span>
        ))}
      </address>
      <div className="gold-hairline my-6 w-16" />
      <p className="text-xs text-slate-500">{note}</p>
    </div>
    <div className="mt-auto">
      <iframe
        data-testid={mapTestId}
        title={`Map — ${city}`}
        src={office.mapEmbed}
        loading="lazy"
        className="h-44 w-full border-0 border-t border-navy/10 grayscale-[35%] transition-all duration-500 hover:grayscale-0"
      />
      <a
        data-testid={`${mapTestId}-link`}
        href={office.mapLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 border-t border-navy/10 bg-white px-9 py-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-royal transition-colors hover:bg-navy hover:text-gold"
      >
        <MapPin size={14} /> Open in Google Maps
      </a>
    </div>
  </div>
);

const Contact = () => {
  const openSamples = useContext(SampleModalContext);

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Chapter
          index="08"
          eyebrow="Contact the Export Desk"
          title="Begin with a sample."
          description="Share your composition, weight and finish requirements — mill-matched samples are couriered within days of confirmation."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <OfficeCard office={CONTACT.bhagalpur} city="Bhagalpur, Bihar" note="Weaving, dyeing, finishing and inspection." testId="contact-bhagalpur-office-card" mapTestId="contact-bhagalpur-map" />
          </Reveal>
          <Reveal delay={0.1}>
            <OfficeCard office={CONTACT.kolkata} city="Kolkata, West Bengal" note="Documentation, freight and buyer coordination." testId="contact-kolkata-office-card" mapTestId="contact-kolkata-map" />
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-6">
          <div className="grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4">
            <a
              data-testid="contact-email-link"
              href={`mailto:${CONTACT.email}`}
              className="group flex items-center gap-4 bg-white p-6 transition-colors hover:bg-navy"
            >
              <Mail size={18} className="shrink-0 text-gold" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-300">Export enquiries</div>
                <div className="text-sm font-medium text-navy group-hover:text-white">{CONTACT.email}</div>
                <div className="text-xs text-slate-400 group-hover:text-slate-300">{CONTACT.emailAlt}</div>
              </div>
            </a>
            <a
              data-testid="contact-phone-link"
              href={`tel:${CONTACT.phoneHref}`}
              className="group flex items-center gap-4 bg-white p-6 transition-colors hover:bg-navy"
            >
              <Phone size={18} className="shrink-0 text-gold" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-300">Phone</div>
                <div className="text-sm font-medium text-navy group-hover:text-white">{CONTACT.phone}</div>
              </div>
            </a>
            <a
              data-testid="contact-whatsapp-link"
              href={`https://wa.me/${CONTACT.phoneHref.replace("+", "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 bg-white p-6 transition-colors hover:bg-navy"
            >
              <MessageCircle size={18} className="shrink-0 text-gold" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-300">WhatsApp</div>
                <div className="text-sm font-medium text-navy group-hover:text-white">Message the export desk</div>
              </div>
            </a>
            <div data-testid="contact-office-hours" className="flex items-center gap-4 bg-white p-6">
              <Clock size={18} className="shrink-0 text-gold" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Office hours</div>
                <div className="text-sm font-medium text-navy">{CONTACT.hours}</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.18} className="mt-6">
          <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.3em] text-royal">Follow the mill</p>
          <SocialCards />
        </Reveal>

        <Reveal delay={0.2} className="mt-16">
          <div className="grain-overlay relative flex flex-col items-center bg-navy px-6 py-16 text-center sm:px-12">
            <p className="font-serif text-3xl font-semibold text-white sm:text-4xl">
              Tell us your fabric. <em className="text-gold">We will weave it.</em>
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">
              <span data-testid="contact-sample-swatch-note">Share your sample swatch or</span> send your company details and fabric requirements — our export desk responds within one business day.
            </p>
            <button
              data-testid="contact-request-samples-btn"
              onClick={openSamples}
              className="group mt-8 flex items-center gap-3 bg-gold px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-navy transition-colors duration-300 hover:bg-champagne"
            >
              Request Samples
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
